import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

import { useAuthImage } from "@/hooks/useAuthImage";
import { Badge } from "@/components/ui/badge";
import { unvalidateChapter } from "@/api/bd";
import ModerationReformulationCard from "./ModerationReformulationCard";
import PlancheLightbox from "./PlancheLightbox";

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api/bd`
  : "/api/bd";

function ThumbnailImage({ token, imageUrl }) {
  const { src } = useAuthImage(imageUrl, token);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  if (!src) return <div className="bg-gray-100 w-full h-48 rounded animate-pulse" />;
  return (
    <>
      <button
        type="button"
        onClick={() => setLightboxOpen(true)}
        className="block w-full cursor-zoom-in bg-transparent p-0 text-left"
        aria-label="Agrandir cette version de planche BD"
      >
        <img src={src} alt="version" className="w-full rounded border" />
      </button>
      {lightboxOpen && (
        <PlancheLightbox
          src={src}
          alt="Version de planche BD"
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </>
  );
}

export default function BDVersionsPanel({
  token,
  clientId,
  chapterIndex,
  chapterMeta,
  onClose,
  onChanged,
  onRegen,
}) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const chapterMetaSignalRef = useRef(null);

  const reload = async () => {
    setErr(null);
    try {
      const res = await axios.get(
        `${API}/chapters/${clientId}-${chapterIndex}/versions`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setData(res.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    }
  };

  useEffect(() => {
    chapterMetaSignalRef.current = null;
    reload();
  }, [clientId, chapterIndex]); // eslint-disable-line

  // Le refresh() de BDChat met à jour `meta` chez le parent, mais ne recharge
  // pas les données propres à ce panel. On resynchronise les changements qui
  // peuvent arriver pendant que le chapitre reste ouvert.
  useEffect(() => {
    const signal = [
      chapterMeta?.status || "",
      chapterMeta?.n_versions ?? 0,
      chapterMeta?.validated_outdated ? "outdated" : "",
      chapterMeta?.proposed_reformulation?.created_at || "",
    ].join("|");
    if (chapterMetaSignalRef.current === null) {
      chapterMetaSignalRef.current = signal;
      return;
    }
    chapterMetaSignalRef.current = signal;
    if (chapterMeta) reload();
    // eslint-disable-next-line
  }, [
    chapterMeta?.status,
    chapterMeta?.n_versions,
    chapterMeta?.validated_outdated,
    chapterMeta?.proposed_reformulation?.created_at,
  ]);

  const handleValidate = async (versionId) => {
    if (busy) return;
    if (!window.confirm("Valider cette version comme officielle ?")) return;
    setBusy(true);
    try {
      await axios.post(
        `${API}/chapters/${clientId}-${chapterIndex}/versions/${versionId}/validate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      await reload();
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleUnvalidate = async () => {
    if (busy) return;
    if (!window.confirm("Dévalider ce chapitre ? Tu pourras le régénérer puis valider une autre version.")) return;
    setBusy(true);
    try {
      await unvalidateChapter(token, `${clientId}-${chapterIndex}`);
      await reload();
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleRestore = async (versionId) => {
    if (busy) return;
    setBusy(true);
    try {
      await axios.post(
        `${API}/chapters/${clientId}-${chapterIndex}/versions/${versionId}/restore`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(
        "Version reprise dans le fil — tu peux la modifier depuis la conversation."
      );
      if (onChanged) await onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const labelChapter = chapterIndex === 6 ? "Planche complète" : `Chapitre ${chapterIndex}`;
  const chapterValidated = data?.status === "validated" || data?.validated || data?.validated_at;

  return (
    <div className="w-96 border-l bg-white flex flex-col h-full">
      <header className="border-b px-4 py-3 flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-semibold">{labelChapter}</h3>
            {data?.validated_outdated ? (
              <Badge className="bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-100">
                ⚠ Validée ≠ dernière
              </Badge>
            ) : chapterValidated && (
              <Badge className="bg-green-100 text-green-800 border-green-200 hover:bg-green-100">
                ✓ Validée
              </Badge>
            )}
          </div>
          {data?.validated_outdated && (
            <p className="text-xs text-amber-800 mt-2">
              La version validée n'est pas la plus récente de l'historique. C'est bien elle qui sera utilisée pour la planche complète et le mini-site — si c'est ce que tu veux, il n'y a rien à faire. Pour utiliser une version plus récente, clique « Valider celle-ci » dessus.
            </p>
          )}
          {data && (
            <p className="text-xs text-gray-500">
              {data.versions.length} version(s)
            </p>
          )}
        </div>
        <button
          onClick={onClose}
          className="text-gray-500 hover:text-gray-800 text-xl px-2"
          title="Fermer"
        >
          ×
        </button>
      </header>
      <div className="flex-1 overflow-y-auto p-3 space-y-4">
        {err && <p className="text-red-600 text-sm">{err}</p>}
        {data && data.versions.length === 0 && (
          <p className="text-gray-500 text-sm text-center mt-8">
            Aucune version générée pour ce chapitre.
          </p>
        )}
        {data?.proposed_reformulation && (
          <ModerationReformulationCard
            token={token}
            clientId={clientId}
            chapterIndex={chapterIndex}
            proposedReformulation={data.proposed_reformulation}
            onRefresh={async () => {
              await reload();
              if (onChanged) onChanged();
            }}
            onRegen={onRegen}
          />
        )}
        {data && [...data.versions].reverse().map((v) => {
          const versionValidated = Boolean(v.is_validated || v.validated || v.validated_at);
          return (
            <div
              key={v.version_id}
              className={`border rounded p-2 ${
                versionValidated ? "border-green-500 bg-green-50" : "border-gray-200"
              }`}
            >
              <ThumbnailImage token={token} imageUrl={v.image_url} />
              <div className="flex items-center justify-between mt-2">
                <div className="text-xs text-gray-500">
                  <div>{v.created_at?.slice(0, 16).replace("T", " ") || "—"}</div>
                  <div>{v.cost_estimated?.toFixed(2)} €</div>
                  {v.feedback && <div>{v.feedback === "up" ? "👍" : "👎"}</div>}
                </div>
                <div className="flex items-center gap-2">
                  {versionValidated ? (
                    <>
                      <Badge className="bg-green-600 text-white border-green-600 hover:bg-green-600">
                        ✓ Validée
                      </Badge>
                      <button
                        onClick={handleUnvalidate}
                        disabled={busy}
                        className="border border-gray-300 text-gray-600 text-xs px-2 py-1 rounded hover:bg-gray-100 disabled:opacity-50"
                        title="Dévalider ce chapitre pour pouvoir le régénérer"
                      >
                        Dévalider
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => handleValidate(v.version_id)}
                      disabled={busy}
                      className="bg-green-600 text-white text-xs px-2 py-1 rounded hover:bg-green-700 disabled:opacity-50"
                    >
                      Valider celle-ci
                    </button>
                  )}
                  <button
                    onClick={() => handleRestore(v.version_id)}
                    disabled={busy}
                    className="border border-gray-300 text-gray-600 text-xs px-2 py-1 rounded hover:bg-gray-100 disabled:opacity-50"
                    title="Remet cette version dans la conversation pour pouvoir la modifier ou la régénérer. Gratuit."
                  >
                    ↩ Reprendre dans le fil
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
